// components/b2b/SpendAnalysis.jsx
import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { useBrand } from '../../contexts/BrandContext';
import { CATALOG } from '../../config/catalog';

export function SpendAnalysis({ onNavigate }) {
  const { brand } = useBrand();
  const [timeframe, setTimeframe] = useState('ytd');
  
  // Calculate totals
  const totalGpo = CATALOG.reduce((sum, item) => sum + item.gpo, 0);
  const totalDist = CATALOG.reduce((sum, item) => sum + item.dist, 0);
  const totalDirect = CATALOG.reduce((sum, item) => sum + item.direct, 0);
  
  const gpoSavings = totalGpo - totalDirect;
  const distSavings = totalDist - totalDirect;
  const avgSavingsPercent = Math.round(((totalGpo - totalDirect) / totalGpo) * 100);
  
  // Category breakdown
  const categories = ['PPE', 'Pharma', 'Devices', 'Med/Surg', 'Surgical'];
  const categoryData = categories.map(cat => {
    const items = CATALOG.filter(item => item.category === cat);
    const gpoTotal = items.reduce((sum, item) => sum + item.gpo, 0);
    const directTotal = items.reduce((sum, item) => sum + item.direct, 0);
    const savings = Math.round(((gpoTotal - directTotal) / gpoTotal) * 100);
    return { category: cat, savings, count: items.length };
  });
  
  // Mock facility data
  const facilities = [
    { name: 'Regional Medical Center', spend: 147200, savings: 42800, savingsPercent: 29 },
    { name: 'Outpatient Clinic East', spend: 56300, savings: 16400, savingsPercent: 29 },
    { name: 'ASC North', spend: 28900, savings: 8400, savingsPercent: 29 },
    { name: 'Long-Term Care Facility', spend: 14800, savings: 4300, savingsPercent: 29 }
  ];
  
  const totalSpend = facilities.reduce((sum, f) => sum + f.spend, 0);
  const totalSavings = facilities.reduce((sum, f) => sum + f.savings, 0);
  
  return (
    <div style={{
      background: '#F9FAFB',
      minHeight: '100vh',
      padding: '40px 24px',
      fontFamily: 'Libre Franklin, sans-serif'
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
          marginBottom: 32,
          flexWrap: 'wrap',
          gap: 16
        }}>
          <div>
            <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 8, color: brand.c.navy }}>
              Spend Analysis
            </h1>
            <p style={{ color: '#6B7280' }}>
              Track your savings across facilities and categories
            </p>
          </div>
          
          <select
            value={timeframe}
            onChange={e => setTimeframe(e.target.value)}
            style={{
              padding: '10px 16px',
              border: '2px solid #E5E7EB',
              borderRadius: 8,
              fontSize: 14,
              fontFamily: 'Libre Franklin, sans-serif',
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            <option value="ytd">Year to Date</option>
            <option value="q4">Q4 2024</option>
            <option value="q3">Q3 2024</option>
            <option value="q2">Q2 2024</option>
          </select>
        </div>
        
        {/* Summary Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 20,
          marginBottom: 48
        }}>
          <Card>
            <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 8, fontWeight: 600 }}>
                  Total Direct Spend
                </div>
                <div style={{
                  fontSize: 36,
                  fontWeight: 900,
                  color: brand.c.accent,
                  fontFamily: "'Merriweather', serif"
                }}>
                  ${(totalSpend / 1000).toFixed(0)}K
                </div>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                  Across {facilities.length} facilities
                </div>
              </div>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 10,
                background: `${brand.c.accent}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Icon name="dollar" size={24} color={brand.c.accent} />
              </div>
            </div>
          </Card>
          
          <Card>
            <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 8, fontWeight: 600 }}>
                  Savings vs GPO
                </div>
                <div style={{
                  fontSize: 36,
                  fontWeight: 900,
                  color: '#10B981',
                  fontFamily: "'Merriweather', serif"
                }}>
                  ${(totalSavings / 1000).toFixed(0)}K
                </div>
                <div style={{ fontSize: 12, color: '#10B981', marginTop: 4, fontWeight: 600 }}>
                  +{avgSavingsPercent}% average
                </div>
              </div>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 10,
                background: '#D1FAE515',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Icon name="trendUp" size={24} color="#10B981" />
              </div>
            </div>
          </Card>
          
          <Card>
            <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 8, fontWeight: 600 }}>
                  Annual Projection
                </div>
                <div style={{
                  fontSize: 36,
                  fontWeight: 900,
                  color: '#7C3AED',
                  fontFamily: "'Merriweather', serif"
                }}>
                  ${((totalSavings * 4) / 1000).toFixed(0)}K
                </div>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                  Based on current rate
                </div>
              </div>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 10,
                background: '#7C3AED15',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Icon name="barChart" size={24} color="#7C3AED" />
              </div>
            </div>
          </Card>
        </div>
        
        {/* Savings Breakdown */}
        <Card style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 24, color: brand.c.navy }}>
            Cost Comparison
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 20,
            marginBottom: 24
          }}>
            <div style={{
              padding: 20,
              background: '#FEE2E2',
              borderRadius: 12,
              textAlign: 'center'
            }}>
              <div style={{ fontSize: 14, color: '#991B1B', marginBottom: 8, fontWeight: 600 }}>
                GPO Pricing
              </div>
              <div style={{ fontSize: 32, fontWeight: 900, color: '#EF4444' }}>
                ${totalGpo.toFixed(2)}
              </div>
              <div style={{ fontSize: 12, color: '#991B1B', marginTop: 4 }}>
                Per unit total
              </div>
            </div>
            
            <div style={{
              padding: 20,
              background: '#FEF3C7',
              borderRadius: 12,
              textAlign: 'center'
            }}>
              <div style={{ fontSize: 14, color: '#92400E', marginBottom: 8, fontWeight: 600 }}>
                Distributor Pricing
              </div>
              <div style={{ fontSize: 32, fontWeight: 900, color: '#F59E0B' }}>
                ${totalDist.toFixed(2)}
              </div>
              <div style={{ fontSize: 12, color: '#92400E', marginTop: 4 }}>
                Per unit total
              </div>
            </div>
            
            <div style={{
              padding: 20,
              background: '#D1FAE5',
              borderRadius: 12,
              textAlign: 'center'
            }}>
              <div style={{ fontSize: 14, color: '#065F46', marginBottom: 8, fontWeight: 600 }}>
                Direct Pricing
              </div>
              <div style={{ fontSize: 32, fontWeight: 900, color: '#10B981' }}>
                ${totalDirect.toFixed(2)}
              </div>
              <div style={{ fontSize: 12, color: '#065F46', marginTop: 4 }}>
                Per unit total
              </div>
            </div>
          </div>
          
          <div style={{
            padding: 20,
            background: '#EFF6FF',
            borderRadius: 12,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16
          }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1E40AF', marginBottom: 4 }}>
                Your Savings vs GPO: ${gpoSavings.toFixed(2)} ({avgSavingsPercent}%)
              </div>
              <div style={{ fontSize: 14, color: '#3B82F6' }}>
                Based on {CATALOG.length} products in catalog comparison
              </div>
            </div>
            <Button variant="accent" size="sm" icon="barChart">
              View Full Report
            </Button>
          </div>
        </Card>
        
        {/* Category Breakdown */}
        <Card style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 24, color: brand.c.navy }}>
            Savings by Category
          </h2>
          
          <div style={{ display: 'grid', gap: 16 }}>
            {categoryData.map(cat => (
              <div
                key={cat.category}
                style={{
                  padding: 16,
                  background: '#F9FAFB',
                  borderRadius: 8
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 12
                }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: brand.c.navy, marginBottom: 4 }}>
                      {cat.category}
                    </div>
                    <div style={{ fontSize: 13, color: '#6B7280' }}>
                      {cat.count} products
                    </div>
                  </div>
                  <div style={{
                    padding: '6px 14px',
                    background: '#D1FAE5',
                    borderRadius: 6,
                    fontSize: 16,
                    fontWeight: 700,
                    color: '#065F46'
                  }}>
                    -{cat.savings}%
                  </div>
                </div>
                
                <div style={{
                  height: 8,
                  background: '#E5E7EB',
                  borderRadius: 4,
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${cat.savings}%`,
                    background: `linear-gradient(90deg, #10B981, #059669)`,
                    borderRadius: 4,
                    transition: 'width 0.5s ease-out'
                  }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
        
        {/* Facility Breakdown */}
        <Card>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 24, color: brand.c.navy }}>
            Savings by Facility
          </h2>
          
          <div style={{ display: 'grid', gap: 16 }}>
            {facilities.map((facility, i) => (
              <div
                key={i}
                style={{
                  padding: 20,
                  background: '#F9FAFB',
                  borderRadius: 12,
                  border: '1px solid #E5E7EB'
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'start',
                  marginBottom: 12
                }}>
                  <div>
                    <h3 style={{ fontSize: 18, fontWeight: 700, color: brand.c.navy, marginBottom: 4 }}>
                      {facility.name}
                    </h3>
                    <div style={{ fontSize: 14, color: '#6B7280' }}>
                      Total Spend: ${facility.spend.toLocaleString()}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 24, fontWeight: 900, color: '#10B981' }}>
                      ${facility.savings.toLocaleString()}
                    </div>
                    <div style={{ fontSize: 13, color: '#065F46', fontWeight: 600 }}>
                      {facility.savingsPercent}% saved
                    </div>
                  </div>
                </div>
                
                <div style={{
                  height: 6,
                  background: '#E5E7EB',
                  borderRadius: 3,
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${facility.savingsPercent}%`,
                    background: '#10B981',
                    borderRadius: 3
                  }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}